import { Euler, Quaternion, Vector3 } from 'three';

export interface ShipSample {
  /** Authoritative server time for this sample. */
  time: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  roll: number;
}

export interface InterpolatedPose {
  position: Vector3;
  quaternion: Quaternion;
}

interface BufferedSample {
  time: number;
  position: Vector3;
  quaternion: Quaternion;
}

/**
 * Snapshot buffer for a single networked pose (the shared ship).
 *
 * Pushes authoritative samples keyed by server time and renders at
 * `renderTime = waveClock - interpolationDelay`, lerping position and slerping
 * rotation between the two surrounding samples. Clamps to the newest sample
 * instead of extrapolating, so lag stays bounded.
 */
export class InterpolationSystem {
  private samples: BufferedSample[] = [];

  constructor(private readonly maxSamples = 48) {}

  reset(): void {
    this.samples = [];
  }

  push(sample: ShipSample): void {
    const quaternion = new Quaternion().setFromEuler(
      new Euler(sample.pitch, sample.yaw, sample.roll, 'YXZ'),
    );
    this.samples.push({
      time: sample.time,
      position: new Vector3(sample.x, sample.y, sample.z),
      quaternion,
    });
    this.samples.sort((a, b) => a.time - b.time);
    while (this.samples.length > this.maxSamples) this.samples.shift();
  }

  get latestTime(): number | null {
    return this.samples.length ? this.samples[this.samples.length - 1].time : null;
  }

  sample(renderTime: number): InterpolatedPose | null {
    if (this.samples.length === 0) return null;
    if (this.samples.length === 1) {
      const only = this.samples[0];
      return { position: only.position.clone(), quaternion: only.quaternion.clone() };
    }

    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    if (renderTime <= first.time) {
      return { position: first.position.clone(), quaternion: first.quaternion.clone() };
    }
    if (renderTime >= last.time) {
      return { position: last.position.clone(), quaternion: last.quaternion.clone() };
    }

    for (let i = 0; i < this.samples.length - 1; i += 1) {
      const a = this.samples[i];
      const b = this.samples[i + 1];
      if (renderTime >= a.time && renderTime <= b.time) {
        const span = b.time - a.time || 1;
        const alpha = (renderTime - a.time) / span;
        const position = a.position.clone().lerp(b.position, alpha);
        const quaternion = a.quaternion.clone().slerp(b.quaternion, alpha);
        return { position, quaternion };
      }
    }

    return { position: last.position.clone(), quaternion: last.quaternion.clone() };
  }
}
